import type { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { Sites } from '@ps/platform-message-framework/common/Sites';
import { UILayout } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import { merge } from 'lodash';
import type { DeepPartial } from 'redux';

import { select } from '../../../index';
import type { AccountDisplayTheme, Model, RootState, SelectorCreator, Slicer } from '../../types';
import { DisplayTheme } from '../../types';
import { PromptTourType } from '../../types/Cashier';
import type { CashierStorage, CesFeedBackForm, OnboardingCashier } from '../../types/Cashier';
import type { Notification } from '../../types/Notification';
import type { HGLocalStorage, PokerStorage } from '../../types/Poker';

export interface LocalSettingsState {
  cashier?: CashierStorage;
  displayMode?: AccountDisplayTheme;
  documentsSubmitted?: boolean;
  lastLoginAcknowledged?: number;
  layoutMode?: string;
  lowBalancePrompt?: number;
  playingInsights?: number;
  playingInsightsFeedback?: number;
  playSessionLimitGR?: Notification;
  redirectTo?: string;
  registrationBrand?: BrandTypes;
  remainingInactivitySeconds?: number;
  restrictPlayingTime?: Notification | null;
  sessionSummary?: Notification | null;
  showBalance?: boolean;
  validateAccountDays?: number | null;
  homegames?: HGLocalStorage;
  poker?: PokerStorage;
  clientVersion?: string;
  requestBrand?: BrandTypes;
  requestSite?: Sites;
}

export interface LocalSettingsModel extends Model<LocalSettingsState> {
  reducers: typeof reducers;
  selectors: typeof selectors;
}

const defaultCashierTour: OnboardingCashier = {
  onboardingFinished: false,
  onboardingModalShown: false,
  lastModalShown: false
};

const defaultCesFeedBackForm: CesFeedBackForm = {
  convertCurrencyExpireDate: 0,
  depositExpireDate: 0,
  p2pTransferExpireDate: 0,
  transactionHistoryExpireDate: 0,
  withdrawExpireDate: 0
};

const defaultUserSeenPrompt: Partial<Record<PromptTourType, boolean>> = {
  [PromptTourType.Cashier]: false,
  [PromptTourType.FirstDeposit]: false,
  [PromptTourType.FirstWithdraw]: false
};

const defaultCashier: CashierStorage = {
  cesFeedbackForm: defaultCesFeedBackForm,
  disableFeePrompt: false,
  disableWithdrawConfirmationPage: false,
  disableWithdrawFeePrompt: false,
  cashierTour: defaultCashierTour,
  emailVerificationDateTime: '',
  userSeenPrompt: defaultUserSeenPrompt
};

const defaultDisplayMode: AccountDisplayTheme = {
  theme: DisplayTheme.Device
};

const defaultState: LocalSettingsState = {
  cashier: defaultCashier,
  documentsSubmitted: false,
  layoutMode: UILayout.Table,
  showBalance: true,
  homegames: { gameTileView: {} },
  poker: {
    spinAndGo: {
      onboardingFinished: false,
      onboardingModalShown: false
    },
    tournaments: {
      favoriteTournaments: {
        similar: [],
        specific: []
      }
    }
  }
};

const reducers = {
  'persist/REHYDRATE': (state: LocalSettingsState, { localSettings }: Partial<RootState> = {}) => ({
    ...state,
    ...localSettings
  }),
  set: (state: LocalSettingsState, payload: DeepPartial<LocalSettingsState> = {}) =>
    merge({}, state, payload)
};

const selectors = (slice: Slicer<LocalSettingsState>, createSelector: SelectorCreator) => ({
  get() {
    return slice((state) => state || {});
  },
  getRegistrationBrand() {
    return slice((state) => state.registrationBrand as BrandTypes);
  },
  getRegistrationResolvedBrand() {
    return createSelector(
      this.getRegistrationBrand,
      (rootState: RootState) => select.device.isMobileEmbedded(rootState),
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (registrationBrand, isMobileEmbedded, brand) => {
        const mobileBrand = canUseDOM && window?.starsweb?.app?.brand;
        return isMobileEmbedded && mobileBrand ? mobileBrand : registrationBrand || brand;
      }
    );
  },
  getCashier() {
    return slice((state) => state.cashier || defaultCashier);
  },
  getDisplayMode() {
    return slice((state) => state.displayMode || defaultDisplayMode);
  }
});

const localSettings: LocalSettingsModel = {
  state: defaultState,
  reducers,
  selectors
};

export default localSettings;
